import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { Option } from "@/lib/gql/__generated__/graphql";

import type { DnDResultItem } from "@/hooks/use-dnd/use-dnd";

import { BlankContentForList } from "@/components/features/_components";
import { OptionHeaderAndTabs } from "@/components/features/items/_components";
import { ItemListContainer } from "@/components/features/items/edit/_shared/ui/components/system/list/list-wrapper";

import { Table, TableBody } from "@/components/ui/table/table";

import { OptionListPageTableHeader } from "./_ui/table";
import { OptionListPageTableSortableItem } from "./_ui/table/sortable-item";
import { ListTableHeader, SortButton } from "../../../edit/_shared/ui/components/system/list/list-table-header";

type Props = {
	hotelID: string;
	onSaveSort: () => Promise<void>;
	onCancelSort: () => void;

	editOptionPagePath: () => {
		edit: (optionID: string) => string;
		createNewItem: string;
	};

	onDeleteOption: (optionID: string) => Promise<void>;

	dndItemList: Array<DnDResultItem<Option>>;
};

export const OptionListPagePresenter = ({
	hotelID,
	onSaveSort,
	onCancelSort,
	editOptionPagePath,
	onDeleteOption: _onDeleteOption,
	dndItemList,
}: Props) => {
	const navigate = useNavigate();
	const createNewItemList = [
		{
			title: "オプション",
			handleClick: () => {
				navigate(editOptionPagePath().createNewItem);
			},
		},
	];

	/**
	 * オプションをリスト表示できるか
	 */
	const canShowOptionList = useMemo(() => dndItemList.length > 0, [dndItemList.length]);

	const onDeleteOption = useCallback(
		(optionID: string) => {
			return async () => {
				await _onDeleteOption(optionID);
			};
		},
		[_onDeleteOption],
	);

	const onTransitionToEdit = useCallback(
		(optionID: string) => {
			return () => {
				navigate(editOptionPagePath().edit(optionID));
			};
		},
		[editOptionPagePath, navigate],
	);

	const [isSorting, setIsSorting] = useState(false);
	const onChangeIsSorting = useCallback((isSorting: boolean) => setIsSorting(isSorting), []);

	return (
		<>
			<OptionHeaderAndTabs hotelID={hotelID} headerProps={{ title: "オプション一覧" }} />

			<ItemListContainer
				header={
					<ListTableHeader
						leftContent={
							<SortButton
								isSorting={isSorting}
								onChangeIsSorting={onChangeIsSorting}
								onClickCancelSort={onCancelSort}
								onClickSaveSort={onSaveSort}
							/>
						}
						createNewItemList={createNewItemList}
					/>
				}
				tableBody={
					<Table className="w-full min-w-[max-content]">
						{canShowOptionList ? (
							<>
								<OptionListPageTableHeader hasSortItem isSorting={isSorting} />
								<TableBody>
									{dndItemList.map((result) => {
										return (
											<OptionListPageTableSortableItem
												key={result.key}
												isSorting={isSorting}
												onDeleteOption={onDeleteOption(result.value.optionID)}
												editOptionPagePath={editOptionPagePath()}
												onTransitionToEdit={onTransitionToEdit(result.value.optionID)}
												option={result}
											/>
										);
									})}
								</TableBody>
							</>
						) : (
							<BlankContentForList
								content={`販売中のオプションはありません。\n新規作成ボタンからオプションを作成してください。`}
							/>
						)}
					</Table>
				}
			/>
		</>
	);
};
